/** The computer's health in plain words, from the agent's latest heartbeat. Pure; `now` is injectable. */
import type { Health, Usage } from './schemas'

export interface HealthRow {
  label: string
  /** Plain words: "Up for 3 days", "Plenty of room". */
  words: string
  /** The number behind it, small and in mono. */
  value: string | null
  /** Only for a person to look at: nearly full, not heard from. Never a raw error. */
  attention?: boolean
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

export function uptimeWords(seconds: number): string {
  if (seconds < 120) return 'Just started'
  const m = Math.floor(seconds / 60)
  if (m < 60) return `Up for ${plural(m, 'minute')}`
  const h = Math.floor(m / 60)
  if (h < 48) return `Up for ${plural(h, 'hour')}`
  return `Up for ${plural(Math.floor(h / 24), 'day')}`
}

/** A usage as a percent: a bare number is already one; `{ used, total }` is any one unit. */
export function usagePercent(u: Usage | undefined): number | null {
  if (u === undefined) return null
  if (typeof u === 'number') return Math.max(0, Math.min(100, u))
  if ('usedPercent' in u) return Math.max(0, Math.min(100, u.usedPercent))
  return u.total > 0 ? Math.max(0, Math.min(100, (u.used / u.total) * 100)) : null
}

function roomWords(pct: number): { words: string; attention: boolean } {
  if (pct < 70) return { words: 'Plenty of room', attention: false }
  if (pct < 90) return { words: 'Getting full', attention: false }
  return { words: 'Nearly full · we\'re on it', attention: true }
}

export function loadWords(load: number | number[] | undefined): { words: string; value: string } | null {
  const one = Array.isArray(load) ? load[0] : load
  if (one === undefined || !Number.isFinite(one)) return null
  const words = one < 1 ? 'Quiet' : one < 3 ? 'Steady' : 'Busy'
  return { words, value: `load ${one.toFixed(2)}` }
}

export function healthRows(h: Health): HealthRow[] {
  const rows: HealthRow[] = []
  rows.push(h.uptime !== undefined ? { label: 'Running', words: uptimeWords(h.uptime), value: null } : { label: 'Running', words: 'Not reported yet', value: null })
  for (const [label, u] of [['Memory', h.memory], ['Storage', h.disk]] as const) {
    const pct = usagePercent(u)
    if (pct === null) rows.push({ label, words: 'Not reported yet', value: null })
    else rows.push({ label, ...roomWords(pct), value: `${Math.round(pct)}% used` })
  }
  const load = loadWords(h.cpu?.load)
  rows.push(load ? { label: 'Work right now', ...load } : { label: 'Work right now', words: 'Not reported yet', value: null })
  return rows
}

/** How long since the computer last reported, in words; quiet for over five minutes needs a person to look. */
export function heardFrom(at: string | null, now = Date.now()): { words: string; quiet: boolean } {
  if (!at) return { words: 'Waiting for its first report', quiet: false }
  const s = Math.max(0, Math.floor((now - Date.parse(at)) / 1000))
  if (s < 60) return { words: 'Reported just now', quiet: false }
  const m = Math.floor(s / 60)
  if (m < 5) return { words: `Reported ${plural(m, 'minute')} ago`, quiet: false }
  return { words: `Quiet for ${m < 60 ? plural(m, 'minute') : plural(Math.floor(m / 60), 'hour')} · we're looking`, quiet: true }
}
